import React, { useState, useRef, useEffect, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import styled from "styled-components";

import SearchBar from "../components/search/searchBar";
import PageContainer from "../components/containers/PageContainer";
import ModalInfo from "../components/nav/ModalInfo";
import useFeed from "../hooks/useFeed";
import { TailSpin } from "react-loader-spinner";
import LogoBackground from "../assets/img/logos/logoBackground.svg";
import { Feed } from "../types/Feed";
import useCountSignals from "../hooks/useCountSignals";
import Calendar from "../components/containers/Calendar";
import { useFollowedFiles } from "../hooks/useFile";
import FileSum from "../components/blocks/fileSum";
import useMainKPIs from "../hooks/useMainKPIs";
import DateRangePicker from "../components/cta/DateRangePicker";
import { lazy } from "react";
import dynamicIconImports from "lucide-react/dynamicIconImports";
import useCalendar from "../hooks/useCalendar";

const ChevronLeft = lazy(dynamicIconImports["chevron-left"]);
const ChevronRight = lazy(dynamicIconImports["chevron-right"]);
const CalendarIcon = lazy(dynamicIconImports["calendar"]);

// Create a more visible loading placeholder for home page icons
const IconLoadingPlaceholder = () => (
  <div
    style={{
      width: "16px",
      height: "16px",
      backgroundColor: "#f0f0f0",
      borderRadius: "2px",
      opacity: 0.5,
    }}
  />
);

const HomeHero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 25px;
  width: 100%;
  box-sizing: border-box;
`;

const TitleHeader = styled.h1`
  color: #fff;
  font-family: "Sharp Sans";
  font-size: 28px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
  margin: 0;
  padding: 0;
`;

const BackgroundHeader = styled.img`
  width: 414px;
  box-sizing: border-box;
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 10;
`;

const DescriptionHeader = styled.p`
  color: #fff;
  font-family: "Sharp Sans";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin: 0;
  padding: 0;
  span {
    color: #ff734f;
    font-weight: 700;
  }
`;

const Header = styled.div`
  display: flex;
  height: 135px;
  padding: 0px 40px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 5px;
  align-self: stretch;
  border-radius: 19px;
  background: #232129;
  width: calc(100% - 80px);
  box-sizing: border-box;
  width: 100%;
  position: relative;
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 700px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const HomeModule = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  /* flex: 1 0 0; */
  align-self: stretch;
  border-radius: 18px;
  border: 1px solid var(--Main-stroke-1, #ebebeb);
  box-sizing: border-box;
`;

const HeaderFeed = styled.div`
  display: flex;
  padding: 20px 30px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  width: 100%;
  border-bottom: 1px solid var(--Main-stroke-1, #ebebeb);
  min-height: 50px;
  box-sizing: border-box;
  min-width: 450px;
`;

const HeaderFeedWithIcon = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const CalendarIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #ebebeb;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: #f5f5f5;
    border-color: #ff734f;
  }

  &:focus {
    outline: none;
    border-color: #ff734f;
  }
`;

const DatePickerPopup = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 20px;
  z-index: 1000;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  min-width: 400px;
`;

const FeedTitle = styled.h2`
  color: #3c3c3d;
  font-family: "Sharp Sans";
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.18px;
  margin: 0;
  padding: 0;
`;

const FeedContent = styled.div`
  display: flex;
  padding: 0px 20px;
  padding-bottom: 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
  align-self: stretch;
  box-sizing: border-box;
  max-width: 100%;
`;

// Add scan section styled components
const ScanSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 940px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 40px 0;
`;

const KPIContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const KPICard = styled.div`
  display: flex;
  padding: 24px 28px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 18px;
  border: 1px solid #ebebeb;
  background: #fcfcfc;
  box-sizing: border-box;
  height: 150px;
  width: 250px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f8f8;
  }
`;

const KPINumber = styled.span`
  color: #232129;
  font-family: "Sharp Sans";
  font-size: 32px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.32px;
`;

const KPILabel = styled.span`
  color: #232129;
  font-family: "Sharp Sans";
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`;

const SecondaryRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 15px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
`;

const CalendarHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const CalendarNavButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6e6e6e;

  &:hover {
    background-color: #f5f5f5;
    color: #232129;
  }
`;

const NavCalendar = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
`;
const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  console.log("user :", user);
  const [showModal, setShowModal] = useState(false);
  const { data, loading, error, loadMore } = useFeed(user?.user_id);
  const feedRef = useRef<HTMLDivElement>(null);
  const {
    count,
    loading: countLoading,
    error: countError,
  } = useCountSignals(user?.user_id);
  // Hook for followed files
  const {
    data: followedFiles,
    loading: followedLoading,
    error: followedError,
  } = useFollowedFiles(user?.user_id, 3);

  // Date range state for KPIs
  const [kpiDateRange, setKpiDateRange] = useState<{
    startAt: string;
    endAt: string;
  }>({
    startAt: "",
    endAt: "",
  });

  // Hook for main KPIs
  const {
    data: kpiData,
    loading: kpiLoading,
    error: kpiError,
  } = useMainKPIs(user?.user_id, kpiDateRange.startAt, kpiDateRange.endAt);

  // Calendar state
  const [calendarDate, setCalendarDate] = useState(new Date());

  // Create a stable month key to prevent infinite re-renders
  const monthKey = `${calendarDate.getFullYear()}-${String(
    calendarDate.getMonth() + 1
  ).padStart(2, "0")}`;

  // Calendar hook - fetch data for the current month
  const {
    activities: calendarActivities,
    isLoading: calendarLoading,
    error: calendarError,
  } = useCalendar({ monthKey });

  // Date picker popup state
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Month names in French
  const monthNames = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  const navigateCalendarMonth = (direction: "prev" | "next") => {
    setCalendarDate((prev) => {
      const newDate = new Date(prev);
      if (direction === "prev") {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  // Uppercase to the first letter of the username
  const username =
    user?.username.charAt(0).toUpperCase() + user?.username.slice(1);

  console.log("data :", user);

  useEffect(() => {
    console.log("Loading more");
    const observer = new IntersectionObserver(
      (entries) => {
        const lastEntry = entries[0];
        if (lastEntry.isIntersecting && data?.hasMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.5 }
    );

    const currentFeedRef = feedRef.current;
    if (currentFeedRef) {
      observer.observe(currentFeedRef);
    }

    return () => {
      if (currentFeedRef) {
        observer.unobserve(currentFeedRef);
      }
    };
  }, [data?.hasMore, loading, loadMore]);

  const GetUrl = (data: Feed) => {
    if (data.type === "amendment") {
      return `file/${data.fileId}/amendts/${data.id}`;
    }
    if (data.type === "speech") {
      return `#`;
    }
    return `#`;
  };

  const handleCampaignClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowModal(true);
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setKpiDateRange({
      startAt: startDate,
      endAt: endDate,
    });
  };

  const toggleDatePicker = () => {
    setIsDatePickerOpen(!isDatePickerOpen);
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const calendarButton = document.querySelector("[data-calendar-button]");
      const popup = document.querySelector("[data-date-picker-popup]");

      if (
        isDatePickerOpen &&
        calendarButton &&
        popup &&
        !calendarButton.contains(target) &&
        !popup.contains(target)
      ) {
        setIsDatePickerOpen(false);
      }
    };

    if (isDatePickerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDatePickerOpen]);
  return (
    <PageContainer>
      <HomeHero>
        <SearchContainer>
          <SearchBar />
        </SearchContainer>
        <Header>
          <TitleHeader>Bienvenue {username}</TitleHeader>
          <DescriptionHeader>
            Vous avez{" "}
            <span>
              {count?.count || 0} capteur
              {count?.count === 0 || count?.count === 1 || count?.count === null
                ? ""
                : "s"}
            </span>{" "}
            configuré
            {count?.count === 0 || count?.count === 1 || count?.count === null
              ? ""
              : "s"}
            .
          </DescriptionHeader>
          <BackgroundHeader src={LogoBackground} />
        </Header>
        {showModal && (
          <ModalOverlay>
            <ModalInfo
              title="Fonctionnalité verrouillée"
              description="Ce contenu est bloqué dans la version de Parabole que vous utilisez. Contactez-nous pour plus d'informations."
              onClose={() => setShowModal(false)}
            />
          </ModalOverlay>
        )}
        <HomeModule>
          <HeaderFeed>
            <HeaderFeedWithIcon>
              <FeedTitle>Scan</FeedTitle>

              <CalendarIconButton
                onClick={toggleDatePicker}
                data-calendar-button
              >
                <Suspense fallback={<IconLoadingPlaceholder />}>
                  <CalendarIcon size={16} color="#6e6e6e" />
                </Suspense>
                <DatePickerPopup
                  isOpen={isDatePickerOpen}
                  data-date-picker-popup
                  onClick={(e) => e.stopPropagation()}
                >
                  <DateRangePicker onDateRangeChange={handleDateRangeChange} />
                </DatePickerPopup>
              </CalendarIconButton>
            </HeaderFeedWithIcon>
          </HeaderFeed>

          <ScanSection>
            <KPIContainer>
              <KPICard onClick={() => navigate("/followed-amendments")}>
                <KPINumber>
                  {kpiLoading ? (
                    <TailSpin
                      height="20"
                      width="20"
                      color="#FF734F"
                      ariaLabel="loading"
                    />
                  ) : kpiError ? (
                    "0"
                  ) : (
                    kpiData?.amendments_text_followed || 0
                  )}
                </KPINumber>
                <KPILabel>Amendements sur les textes que vous suivez</KPILabel>
              </KPICard>
              <KPICard onClick={() => navigate("/suggested-files")}>
                <KPINumber>
                  {kpiLoading ? (
                    <TailSpin
                      height="20"
                      width="20"
                      color="#FF734F"
                      ariaLabel="loading"
                    />
                  ) : kpiError ? (
                    "0"
                  ) : (
                    kpiData?.text_not_followed || 0
                  )}
                </KPINumber>
                <KPILabel>Textes qui pourraient vous intéresser</KPILabel>
              </KPICard>
              <KPICard onClick={() => navigate("/suggested-amendments")}>
                <KPINumber>
                  {kpiLoading ? (
                    <TailSpin
                      height="20"
                      width="20"
                      color="#FF734F"
                      ariaLabel="loading"
                    />
                  ) : kpiError ? (
                    "0"
                  ) : (
                    kpiData?.amendments_text_not_followed || 0
                  )}
                </KPINumber>
                <KPILabel>Amendements qui pourraient vous intéresser</KPILabel>
              </KPICard>
            </KPIContainer>
          </ScanSection>
        </HomeModule>
        <SecondaryRow>
          <HomeModule style={{ gap: "15px", flexGrow: "4", minWidth: "700px" }}>
            <HeaderFeed>
              <FeedTitle>Vos dossiers en cours</FeedTitle>
            </HeaderFeed>
            <FeedContent>
              {followedLoading ? (
                <TailSpin
                  height="40"
                  width="40"
                  color="#FF734F"
                  ariaLabel="loading"
                />
              ) : followedError ? (
                <p>Erreur lors du chargement des dossiers suivis</p>
              ) : followedFiles?.Files && followedFiles.Files.length > 0 ? (
                followedFiles.Files.map((file) => (
                  <FileSum
                    key={file.uid}
                    uid={file.uid}
                    name={file.name}
                    wording={file.wording}
                    status={file.status}
                    stageFile={file.stage_file_title}
                    stateFile={file.state_file_title}
                    code={file.state_file_code}
                    live={file.active}
                  />
                ))
              ) : (
                <p>Aucun dossier suivi pour le moment</p>
              )}
            </FeedContent>
          </HomeModule>
          <HomeModule style={{ flexGrow: "1" }}>
            <HeaderFeed>
              <CalendarHeaderContainer>
                <FeedTitle>
                  Votre calendrier - {monthNames[calendarDate.getMonth()]}{" "}
                  {calendarDate.getFullYear()}
                </FeedTitle>
                <NavCalendar>
                  <CalendarNavButton
                    onClick={() => navigateCalendarMonth("prev")}
                  >
                    <Suspense fallback={<IconLoadingPlaceholder />}>
                      <ChevronLeft size={16} color="#6e6e6e" />
                    </Suspense>
                  </CalendarNavButton>
                  <CalendarNavButton
                    onClick={() => navigateCalendarMonth("next")}
                  >
                    <Suspense fallback={<IconLoadingPlaceholder />}>
                      <ChevronRight size={16} color="#6e6e6e" />
                    </Suspense>
                  </CalendarNavButton>
                </NavCalendar>
              </CalendarHeaderContainer>
            </HeaderFeed>
            {calendarLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                }}
              >
                <TailSpin color="#ff734f" height={30} width={30} />
              </div>
            ) : calendarError ? (
              <div
                style={{ padding: "20px", textAlign: "center", color: "#666" }}
              >
                <p>Erreur lors du chargement du calendrier</p>
              </div>
            ) : (
              <Calendar
                activities={calendarActivities}
                currentDate={calendarDate}
              />
            )}
          </HomeModule>
        </SecondaryRow>
      </HomeHero>
    </PageContainer>
  );
};

export default Home;
